/** @module csi/view/_query/DatabaseReport */
define([
	"dojo/_base/declare",
	"dojo/has",
	"underscore",
	"csi",
	"csi/core/extendedPathService",
	"csi/heading/Spec",
	"csi/core/Key",
	"csi/reflection",
	"csi/xml",
	"csi/xml/Query",
	"csi/xml/QueryProcessor",
	"./_Report",
	"./ReportFunctions"
], function(declare, has, _, csi, {parsePathSteps}, Spec, Key, reflection, xml, Query, QueryProcessor, _Report, ReportFunctions) {

	var SERVER_FUNCTIONS = {
		AVG: "averageQuery",
		COUNT: "countQuery",
		MAX: "maxQuery",
		MIN: "minQuery",
		SUM: "sumQuery",
		WEIGHTED_AVG: "weightedAverageQuery"
	};

	var AVG = "AVG";
	var COUNT = "COUNT";
	var SUM = "SUM";
	var WEIGHTED_AVG = "WEIGHTED_AVG";

	function _addSpecArgs(/*Spec*/spec, /*String[]*/args, /*Boolean*/bInMatrix) {
		var qs = spec.querySpec;
		var path = bInMatrix ? qs.pathInMatrix : qs.path;

		if (path) {
			args.push('"' + path + '"');
		} else {
			args.push("null");
		}

		args.push('"' + qs.id + '"');

		// if reporting on vector, then add index
		if (qs.index != null) {
			args.push(qs.index);
		}
	}

	function _calculate(/*Object*/reportObj, /*Node*/reportNode, /*Node?*/serverReportNode, /*Node?*/serverWeightNode, /*Object*/mapNodeValues) {
		// summary: calculate report value for attribute given client values and reported value from server
		var f = ReportFunctions[reportObj.func];
		var s1 = reportObj.spec;
		var s2 = reportObj.spec2;
		var values = mapNodeValues[s1.queryKey];
		var weights = s2 ? mapNodeValues[s2.queryKey] : null;
		var serverDenom = null;
		var denom = reportObj.denom;
		if (denom) {
			if (serverWeightNode) {
				serverDenom = serverWeightNode[denom.denomId];
			} else if (serverReportNode) {
				serverDenom = serverReportNode[denom.denomId];
			}
		}
		var serverValue = serverReportNode ? serverReportNode[s1.querySpec.id] : null;
		var value;

		if (reportObj.vectorSpecs) {
			value = _.map(reportObj.vectorSpecs, function(vspec, i) {
				return f(values, i, weights, serverValue ? serverValue[i] : null, serverDenom);
			});
		} else {
			value = f(values, null, weights, serverValue, serverDenom);
		}

		return value;
	}

	/**
	 * Create node to hold report values
	 * @param {string} url
	 * @param {Cache} cache
	 * @param {Writer} [w]
	 * @return {Node}
	 */
	function _createCustomReportNode(url, cache, w) {
		// Make sure the name is not empty to avoid the rendering of references as "<Empty>".
		// Update on 2012-07-13:
		// 	It was done to fix the defect:
		//		DE10259 - My Home-Site-SKUs: 'Empty' value is displayed in Report row for "Quote" attribute.
		// 	This defect is not reproducible anymore on the trunk.
		//	Moreover, the non-empty name leads to another defect:
		// 		DE12712 - My Home-Style-Season (and other views): Report label is missing from report row.
		// 	I'm making the name empty again to fix DE12712 and hopefully not to reintroduce DE10259.
		return w ? w.createNode(url, xml.TYPE_REPORT) : cache.createClientNode(url, xml.TYPE_REPORT, null, xml.NODE_FLAG_NOEDIT);
	}

	/**
	 * Return expression to use to aggregate attribute values
	 * @param {string} func 
	 * @param {string} queryArgs 
	 * @param {Spec} spec1 
	 * @param {Spec} [spec2] - weight attribute if function is weighted average
	 * @param {boolean} [bInMatrix]
	 * @return {string}
	 */
	function _createExpression(func, limitPath, queryArgs, spec1, spec2, bInMatrix) {
		if (func != COUNT) {
			var args = [];

			_addSpecArgs(spec1, args, bInMatrix);

			// if weighted average, then add path and id
			if (spec2) {
				_addSpecArgs(spec2, args, bInMatrix);
			}

			queryArgs += "," + args.join(",");
		}

		const exp = `${SERVER_FUNCTIONS[func]}(${queryArgs})`;

		// if there is a limit path, then ensure it is not empty before doing aggregation; it is possible
		// that the limit path ends to being empty (such as Styles without any Colorways), and an empty limit
		// path means aggregate all query results rather than none
		return limitPath ? `if(isEmpty(${limitPath}), null, ${exp})` : exp;
	}

	function _createNodeForPath(/*Cache*/cache, /*String*/urlReport, /*ExtendedPath*/ep, /*Object*/pathNodes, /*Writer?*/w) {
		// summary: if Writer passed, then write operations to create report node(s) given path; otherwise, create client nodes to hold report calculations
		var path = ep.getStandardPath();
		var node = pathNodes[path || ""];
		if (!node) {
			// store last relation in path
			var id = ep.endRelationId;

			// create nodes for previous steps in path
			ep.popSteps(1, true);

			var parentNode = _createNodeForPath(cache, urlReport, ep, pathNodes, w);

			// use previous url if there is one
			var url = parentNode[id] || _.uniqueId(urlReport + "/");

			if (w) {
				node = _createCustomReportNode(url, cache, w);
				node.publishAttribute("__Root__", xml.ATTR_REF, urlReport);
				parentNode.publishAttribute(id, xml.ATTR_REF, url);
			} else {
				// if node already in cache, then use it
				node = cache.getNode(url) || _createCustomReportNode(url, cache);
				parentNode[id] = url;
			}

			pathNodes[path] = node;
		}

		return node;
	}

	function _getJoinsForMatrixPath(matrixPath) {
		var steps = parsePathSteps(matrixPath);

		return _.reduce(steps, function(memo, step) {
			return memo + ".join(" + step.a + ((step.key != null) ? ("[" + step.key + "]") : "") + ")";
		}, "");
	}

	/**
	 * Return query that applies to the given spec in a matrix
	 * @param {Spec} spec
	 * @param {QueryLevelFilter} qlf - custom view filters
	 * @param {Object} queryObject - ad-hoc filters
	 * @return {Query}
	 */
	function _getMatrixQuery(spec, qlf, queryObject) {
		var matrixQuery = spec.applyMatrixFilters(new Query(), queryObject);
		qlf.toMatrixQuery(matrixQuery, spec);
		return matrixQuery;
	}

	function _getUniqueSpecs(/*Object[]*/settings) {
		var specs = {};

		_.each(settings, function(reportObj) {
			var s1 = reportObj.spec;
			var s2 = reportObj.spec2;

			specs[s1.queryKey] = s1;

			if (s2) {
				specs[s2.queryKey] = s2;
			}
		});

		return _.values(specs);
	}

	/**
	 * Create map of values by spec queryKey for report attributes
	 * @param {Node[]} nodes
	 * @param {QueryLevelFilter} qlf
	 * @param {Object} queryObject
	 * @param {Cache} cache
	 * @param {Object[]} settings
	 * @return {any[]}
	 */
	function _extractValues(nodes, qlf, queryObject, cache, settings) {
		var specs = _getUniqueSpecs(settings);
		var matrixQueries = {};

		return csi.hashArray(specs, "queryKey", function(spec) {
			var id = spec.querySpec.id;
			var path = spec.querySpec.path;
			var ps = spec.parentSpec;
			var matrixPath = ps?.matrixGroupStandardPath;
			var matrixQuery = matrixQueries[matrixPath];
			var specNodes = nodes;

			if (!matrixQuery && matrixPath) {
				// determine query within matrix
				matrixQuery = matrixQueries[matrixPath] = _getMatrixQuery(ps, qlf, queryObject);
			}

			if (matrixQuery && !matrixQuery.isEmpty()) {
				// if matrix query, then get all nodes in matrix, apply filter, and then use path within matrix going forward
				specNodes = cache.getPathNodesForNodes(specNodes, matrixPath);
				specNodes = QueryProcessor.applyQueryToNodes(matrixQuery, specNodes, cache);
				path = spec.querySpec.pathInMatrix;
			}

			specNodes = cache.getPathNodesForNodes(specNodes, path);

			return _.map(specNodes, function(node) {
				return node ? node[id] : null;
			});
		});
	}

	var DatabaseReport = declare(_Report, {
		_internalAttributes: {
			"Node Name": 1,
			"Node Type": 1,
			__LimitPath__: 1,
			__Query__: 1,
			__Root__: 1
		},

		// Object[]: array of objects each containing (only support for one or two parameter functions for now):
		// 		func: function to use to combine values
		// 		spec: spec for attribute to rollup
		// 		spec2: spec for optional attribute used as parameter to function (weighted average)
		// 		vectorSpecs: array of specs that make up single vector attribute to rollup (each spec will have separate index)
		//settings: null,

		constructor: function(/*Object[]*/settings) {
			this.settings = settings;
		},

		contains: function(/*Spec*/spec) {
			// summary: return true if spec processed by report
			if (!this.inherited(arguments)) {
				return false;
			}

			return _.some(this.settings, function(obj) {
				if (obj.vectorSpecs) {
					return _.some(obj.vectorSpecs, function(vs) {
						return spec.queryKey == vs.queryKey;
					});
				}

				return spec.queryKey == obj.spec.queryKey;
			});
		},

		/**
		 * @override
		 * Write operations to gather necessary data from server
		 */
		generate: function(qr, writer) {
			var settings = this.settings;
			var ctx = qr.getContext();
			var cache = ctx.cache;
			var queryObject = ctx.view.get("queryForServer");
			var qrResult = qr.bResultNode ? qr : qr.parentResult;
			var qlf = qr.level.getFilter();
			var resultUrls = qrResult.getChildResultUrls();
			var missingUrls;
			var nodes = [];

			// determine which results we have in cache and which need to be requested
			if (resultUrls) {
				if (has("csi-no-store-caching")) {
					missingUrls = resultUrls;
				} else {
					const displayedUrls = qrResult.getDisplayedChildResultUrls();

					// determine results not in cache (not displayed)
					missingUrls = _.difference(resultUrls, displayedUrls);

					// determine result nodes in cache (displayed)
					for (const url of displayedUrls) {
						const node = cache.get(url);
						if (node && !reflection.isDividerType(node.$Type)) {
							nodes.push(node);
						}
					}
				}
			} else if (!qr.isRoot()) {
				// if results not known for a group, then cannot generate report yet; need to wait for it to be expanded
				return null;
			}

			// generate report node if does not already exist
			let reportNode = ctx.getNode();
			let serverReportNode;
			let urlReport;

			if (reportNode) {
				urlReport = reportNode.__Report__;
				serverReportNode = cache.get(urlReport);
			} else {
				reportNode = _createCustomReportNode(qr.url, cache);
			}

			if (missingUrls && !missingUrls.length) {
				// if list of urls is empty, then client has all results and server report nodes can be removed
				delete reportNode.__Report__;
				cache.remove(urlReport);
				serverReportNode = urlReport = null;
			} else if (missingUrls && serverReportNode && _.isEqual(missingUrls, serverReportNode.__LimitPath__)) {
				// if urls are being requested and they match the last set of urls reported on, then no need to request again
			} else {
				// request report information from server for nodes not cached on client
				if (!urlReport) {
					urlReport = reportNode.__Report__ = _.uniqueId(qr.url + "/");
				}

				this._generateRequest(qr, writer, qlf, queryObject, cache, urlReport, missingUrls);
			}

			// extract information from nodes already cached on client
			var mapNodeValues = _extractValues(nodes, qlf, queryObject, cache, settings);

			// return callback function to combine the server and client calculations when server request is finished
			return () => {
				var pathNodes = {"": reportNode};

				serverReportNode = cache.get(urlReport);

				_.each(settings, function(reportObj) {
					if (!reportObj.denomId) {
						// combine server and client calculations into final report value
						var qs = reportObj.spec.querySpec;
						var qs2 = reportObj.spec2 && reportObj.spec2.querySpec;
						var serverNode;
						var serverWeightNode;

						if (serverReportNode) {
							serverNode = cache.getPathNode(serverReportNode, qs.path);
							serverWeightNode = qs2 && cache.getPathNode(serverReportNode, qs2.path);
						}

						// if server root node exists for report, the server node along path must also exist to do further calculations -
						// if not, assume the calculations are not possible, such as when cannot report on matrix columns when there are
						// ad-hoc filters in matrix columns and the view is grouped (S10033)
						if (!serverReportNode || serverNode) {
							var node = _createNodeForPath(cache, qr.url, reportObj.spec.getExtendedPath().clone(), pathNodes);
							var value = _calculate(reportObj, node, serverNode, serverWeightNode, mapNodeValues);

							xml.nodeCreateClientAttr(node, qs.id, reportObj.vectorSpecs ? xml.ATTR_DOUBLEVECTOR : xml.ATTR_DOUBLE, value);
						}
					}
				});
			};
		},

		/**
		 * Write operations to generate report results for nodes that are not in cache
		 * @param {QueryResult} qr
		 * @param {Writer} w
		 * @param {QueryLevelFilter} qlf
		 * @param {Object} queryObject
		 * @param {Cache} cache
		 * @param {string} urlReport
		 * @param {string[]} [urls]
		 */
		_generateRequest: function(qr, w, qlf, queryObject, cache, urlReport, urls) {
			var cn = _createCustomReportNode(urlReport, cache, w);
			var pathNodes = {"": cn};
			var store = this._getReportStore(qr);
			var rc = reflection.getClass(qr.level.type);
			var matrixQueries = {};
			var queryString;
			var limitPathExp;

			if (urls) {
				// if urls are provided, then use them as limit path, including query by type (if available) for better performance
				var q = new Query();

				if (rc && !rc.abstract) {
					q.Type(rc.getType());
				}

				queryString = q.toString();
			} else {
				// otherwise, generate request from store
				var request = store.getReportRequest();
				var limitPath = request.getParameter("Qry.Limit.Path");
				if (limitPath) {
					if (!_.isArray(limitPath)) {
						limitPath = [limitPath];
					}

					// unescape limitPath to reverse effects of xml.getUrlPath
					limitPath = limitPath.map((p) => 'cnl("' + unescape(p) + '")');
					limitPathExp = 'list(' + limitPath.join() + ')';
				}

				queryString = request.binaryValue;
			}

			cn.publishAttribute("__Root__", xml.ATTR_REF, urlReport);
			cn.publishAttribute("__Query__", xml.ATTR_STRING, queryString);

			if (urls || limitPathExp) {
				cn.publishAttribute("__LimitPath__", xml.ATTR_REFLIST, urls, null, null, xml.ATTR_FLAG_EVAL_EXPRESSION, limitPathExp);
			}

			// for each setting, create a node for the path (if necessary) and assign the expression with aggregation functions
			_.each(this.settings, function(reportObj, i) {
				var spec = reportObj.spec;
				var ps = spec.parentSpec;
				var qs = spec.querySpec;
				var id = reportObj.denomId || qs.id;
				var matrixPath = ps?.matrixGroupStandardPath;
				var matrixQuery = matrixPath ? _getMatrixQuery(ps, qlf, queryObject) : null;
				var queryNumber = "";
				var expr;
				var queryArgs;
				var type;
				let limitPath;

				if (urls || limitPathExp) {
					queryArgs = "__Root__.__LimitPath__";

					if (matrixPath) {
						// if spec is in matrix, then need to extend limit path to include matrix nodes and add specific query for matrix specs
						queryNumber = matrixQueries[matrixPath];

						if (queryNumber == null) {
							// ensure that type of nodes is in query so access control can be computed on SQL
							const {matrixTargetClass} = spec.rootSpec;

							if (!matrixTargetClass || matrixTargetClass.abstract) {
								// FIX: not handling case of abstract type right now, such as DataSheet - reporting on that matrix should be rare though;
								// but it does happen in other cases, such as if we added a Colors matrix to SupplierItem (which is one case I mocked up to test this)
								console.warn("Report row calculations are not correct for matrix: " + matrixPath);
							} else {
								matrixQuery.setType(matrixTargetClass.type);
							}

							queryNumber = matrixQueries[matrixPath] = i + 1;
							cn.publishAttribute("__Query__" + queryNumber, xml.ATTR_STRING, matrixQuery.toString());

							if (urls) {
								const cnls = urls.map((url) => xml.getUrlPath(url, matrixPath));
								cn.publishAttribute("__LimitPath__" + queryNumber, xml.ATTR_CNLLIST, cnls);
							} else {
								cn.publishAttribute("__LimitPath__" + queryNumber, xml.ATTR_REFLIST, [], false, false, xml.ATTR_FLAG_EVAL_EXPRESSION, "__LimitPath__" + _getJoinsForMatrixPath(matrixPath));
							}
						}

						if (queryNumber) {
							queryArgs += queryNumber;
						} else {
							// if no matrix query, then no special processing needed for matrix
							matrixPath = null;
						}
					}

					limitPath = queryArgs;
				} else if (matrixQuery && !matrixQuery.isEmpty()) {
					// server cannot support report calculation of matrix columns when matrix is ad-hoc filtered and urls are not known (S10033)
					return;
				} else {
					matrixPath = null;
					queryArgs = "null";
				}

				queryArgs += ",__Root__.__Query__" + queryNumber;

				if (reportObj.vectorSpecs) {
					// example expression for vector:
					// vector(
					//        sumQuery(LimitPath, Query, "Child:Attribute/Child:Path", "Attribute_Id", 0),
					//        sumQuery(LimitPath, Query, "Child:Attribute/Child:Path", "Attribute_Id", 1),
					//        ...
					//        sumQuery(LimitPath, Query, "Child:Attribute/Child:Path", "Attribute_Id", n)
					// )
					var args = _.map(reportObj.vectorSpecs, function(vectorSpec) {
						return _createExpression(reportObj.func, limitPath, queryArgs, vectorSpec, reportObj.spec2, !!matrixPath);
					});

					expr = "vector(" + args.join(",") + ")";
					type = xml.ATTR_DOUBLEVECTOR;
				} else {
					// example for simple attribute:
					// sumQuery(LimitPath, Query, "Child:Attribute/Child:Path", "Attribute_Id")
					expr = _createExpression(reportObj.func, limitPath, queryArgs, spec, reportObj.spec2, !!matrixPath);
					type = xml.ATTR_DOUBLE;
				}

				_createNodeForPath(cache, urlReport, spec.getExtendedPath().clone(), pathNodes, w).publishAttribute(id, type, null, null, null, null, expr);
			}, this);
		}
	});

	DatabaseReport.createFromSettings = function(/*_ReportSetting[]*/reportSettings, /*Specs*/specs, /*SpecBuilder*/specBuilder) {
		// summary: convert _ReportSetting objects into runtime specs if possible.
		// 		_ReportSetting = {
		// 			key: attribute to rollup
		// 			key2: optional attribute used as parameter to function (weighted average)
		// 			func: function to use to combine values
		// 		}
		const {getCachedSpec, getKeyForSourceClass} = specBuilder;
		const denomSuffix = "__DENOM";
		const settings = [];

		const getSpec = (key, forWeight) => {
			const keyObj = Key.toKey(key);
			let spec = specs.getByKey(keyObj, true);
			if (!spec) {
				// in a multi-level table, it's possible the report is only for one level
				const keyForLevel = getKeyForSourceClass(key);
				if (keyForLevel) {
					spec = specs.getByKey(keyForLevel, true);

					if (forWeight) {
						spec ||= getCachedSpec(key, true);
					}
				}
			}

			return spec;
		};

		reportSettings?.forEach(({func, key, key2}) => {
			if (!SERVER_FUNCTIONS[func]) {
				console.assert(false);
				return;
			}

			let vectorSpecs;
			let spec2;
			let denom;

			// report may be configured to sum whole vector, whereas specs contains an individual spec for each index
			let spec = getSpec(key);
			if (!spec) {
				const keyObj = Key.toKey(key);
				vectorSpecs = specs.getVectorSpecs(keyObj.id, keyObj.path);
				spec = vectorSpecs[0];
			}

			// if no query spec, then cannot report on it
			if (spec?.querySpec) {
				// add additional settings for AVG and WEIGHTED_AVG to compute denominator so they can be calculated incrementally
				if (func == AVG) {
					// vector ignored in denominator since count will be same for all
					denom = {
						denomId: spec.querySpec.id + denomSuffix,
						func: COUNT,
						spec,
					};
				} else if (func == WEIGHTED_AVG) {
					// find weight spec - spec for function parameter does not have to be displayed in view
					spec2 = key2 ? getSpec(key2, true) : null;

					if (!spec2?.querySpec) {
						return;
					}

					// vector not supported for denominator
					denom = {
						denomId: spec2.querySpec.id + denomSuffix,
						func: SUM,
						spec: spec2
					};
				}

				if (denom) {
					settings.push(denom);
				}

				settings.push({
					denom,
					func,
					spec,
					spec2,
					vectorSpecs,
				});
			}
		});

		return settings.length ? new DatabaseReport(settings) : null;
	};

	return DatabaseReport;
});